import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  useApproveExtranetChange,
  useBulkApproveExtranetChanges,
  useBulkRejectExtranetChanges,
  useRejectExtranetChange,
} from '~/hooks/apis/extranet/useExtranetPendingChanges';

import ApiError from '~/types/ApiError';

import {
  getApprovalFailureMessage,
  getApprovalFailureSnackbarVariant,
  getBulkApproveFailureSummary,
} from '../extranetReplayErrors/approvalFailureHandlers';
import { parseBulkApproveReplayFailures, parseExtranetReplayFailure } from '../extranetReplayErrors/parseReplayFailure';
import type { ExtranetReplayFailure } from '../extranetReplayErrors/types';
import type { ExtranetApprovalAction } from '../types';

interface UseExtranetApprovalActionsOptions {
  onSuccess?: () => void;
}

export default function useExtranetApprovalActions({ onSuccess }: UseExtranetApprovalActionsOptions = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const approveOne = useApproveExtranetChange();
  const rejectOne = useRejectExtranetChange();
  const bulkApprove = useBulkApproveExtranetChanges();
  const bulkReject = useBulkRejectExtranetChanges();

  const [confirmAction, setConfirmAction] = useState<ExtranetApprovalAction | null>(null);
  const [replayFailures, setReplayFailures] = useState<Array<ExtranetReplayFailure>>([]);

  const isMutating = approveOne.isPending || rejectOne.isPending || bulkApprove.isPending || bulkReject.isPending;

  const handleConfirmAction = useCallback(
    async (action: ExtranetApprovalAction) => {
      const { type, ids } = action;
      setConfirmAction(null);
      setReplayFailures([]);

      try {
        if (ids.length === 1) {
          if (type === 'approve') {
            const expectedProposedValue = action.expectedProposedValues?.[ids[0]];
            if (expectedProposedValue === undefined) {
              throw new Error('This change must be refreshed before it can be approved.');
            }
            await approveOne.mutateAsync({ id: ids[0], expectedProposedValue });
          } else {
            await rejectOne.mutateAsync(ids[0]);
          }
          enqueueSnackbar(`Change ${type}d`, { variant: 'success' });
        } else if (type === 'approve') {
          const result = await bulkApprove.mutateAsync(ids);
          const parsedReplayFailures = parseBulkApproveReplayFailures(result);

          if (parsedReplayFailures.length > 0) {
            setReplayFailures(parsedReplayFailures);
            const failureSummary = getBulkApproveFailureSummary(parsedReplayFailures);
            enqueueSnackbar(
              failureSummary
                ? `Failed to approve: ${failureSummary}`
                : `${result.approved} change(s) approved, ${result.skipped} skipped, ${parsedReplayFailures.length} failed`,
              { variant: 'warning' },
            );
          } else {
            enqueueSnackbar(`${result.approved} change(s) approved, ${result.skipped} skipped`, { variant: 'success' });
          }
        } else {
          const result = await bulkReject.mutateAsync(ids);
          enqueueSnackbar(`${result.rejected} change(s) rejected, ${result.skipped} skipped`, { variant: 'success' });
        }
        onSuccess?.();
      } catch (err) {
        if (type === 'approve' && err instanceof ApiError) {
          const failure = parseExtranetReplayFailure(err.responseBody);
          if (failure) {
            setReplayFailures([failure]);
          }
        }

        enqueueSnackbar(getApprovalFailureMessage(err, type), {
          variant: getApprovalFailureSnackbarVariant(err),
        });
      }
    },
    [approveOne, bulkApprove, bulkReject, enqueueSnackbar, onSuccess, rejectOne],
  );

  return {
    confirmAction,
    handleConfirmAction,
    isMutating,
    replayFailures,
    setConfirmAction,
    setReplayFailures,
  };
}
